import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageSquare } from "lucide-react";
import { DealerPortalCommentForm } from "@/components/dealer-portal/DealerPortalCommentForm";
import { CommentList } from "@/admin/leads/comments/CommentList";
import type { Comment } from "@/admin/leads/comments/types";
import { useLanguage } from "@/context/LanguageContext";
import { vehicleCardUILabels, commonMiscLabels } from "@/i18n";
import { useDealerPortal } from "@/hooks/useDealerPortal";
import { useMemo } from "react";
import type { DealerPortalVehicleComment } from "@/types/dealerPortal";

interface DealerPortalVehicleCommentsProps {
  vehicleId: string;
}

function mapVehicleCommentToComment(c: DealerPortalVehicleComment): Comment {
  const author =
    c.actor?.user?.username?.trim() || c.actor?.user?.email || "Unknown";
  return {
    id: c.id,
    text: c.content,
    timestamp: c.createdAt,
    author,
  };
}

/**
 * Kommentarer på fordonskortet – bilhandlare kan lägga kommentarer och tagga kollegor.
 * Uses dealer portal API: GET vehicle includes comments; POST /vehicles/:id/comments to add.
 */
export function DealerPortalVehicleComments({
  vehicleId,
}: DealerPortalVehicleCommentsProps) {
  const { language } = useLanguage();
  const vc = vehicleCardUILabels(language);
  const common = commonMiscLabels(language);
  const { vehicle, colleagues, addVehicleComment } = useDealerPortal({
    vehicleId,
  });
  const isSubmitting = addVehicleComment.isPending;

  const comments: Comment[] = useMemo(() => {
    const list = vehicle.data?.comments ?? [];
    return list.map(mapVehicleCommentToComment);
  }, [vehicle.data?.comments]);

  const colleaguesForMentions = useMemo(() => {
    const list = colleagues.data ?? [];
    return list
      .map((c) => ({
        id: c.id,
        name: c.user
          ? `${c.user.firstName ?? ""} ${c.user.lastName ?? ""}`.trim() ||
            c.user.email
          : "",
      }))
      .filter((c) => c.name);
  }, [colleagues.data]);

  const handleSubmit = async (
    text: string,
    _isPublic: boolean,
    mentions: string[],
  ) => {
    await addVehicleComment.mutateAsync({
      vehicleId,
      content: text.trim(),
      mentions,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-1.5 text-lg">
          <MessageSquare className="h-5 w-5" />
          {common.comments ?? "Kommentarer"}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col h-[400px]">
        <div className="flex-1 overflow-hidden">
          <CommentList
            comments={comments}
            noCommentsYet={
              vc.noCommentsYet ?? "Inga kommentarer än. Börja konversationen!"
            }
          />
        </div>
        <div className="mt-4 pt-4 border-t">
          <DealerPortalCommentForm
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            colleagues={colleaguesForMentions}
            isLoadingColleagues={colleagues.isLoading}
          />
        </div>
      </CardContent>
    </Card>
  );
}
